import { useMemo } from 'react';
import { usePersistedColumnSizing } from '@shared/hooks';
import { ItemsTable, TItem } from '@modules/items';
import { createTableColumn, useTableFeatures } from '@fluentui/react-components';
import { TResolvedData } from '../pages/SearchResults.page';
import { TTableRow } from '@shared/types';
import { TSearchItem } from '../types';
import { Pagination } from '@shared/components';
import { UI_MARGIN_TOP } from '../constants';

const columnIds = ['name', 'location', 'latestVersionId', 'approvedVersionId', 'authors'] as const;

const columnSizingParams = {
  autoFitColumns: false,
  columnSizingOptions: {
    name: { idealWidth: 350, minWidth: 175 },
    location: { idealWidth: 250, minWidth: 125 },
    latestVersionId: { defaultWidth: 100 },
    approvedVersionId: { defaultWidth: 100 },
    authors: { defaultWidth: 100 },
  },
};

const tableId = 'search-results-table';

const SearchResultsContent = ({ items, total, resolvedNames }: TResolvedData) => {
  const columns = useMemo(
    () => columnIds.map((columnId) => createTableColumn<TSearchItem | TItem>({ columnId })),
    [columnIds]
  );
  const { columnSizing_unstable, getRows } = useTableFeatures({ columns, items, getRowId: (item) => item._id }, [
    usePersistedColumnSizing(tableId, columnSizingParams),
  ]);

  /* Map raw rows to fluent-table props */
  const rows: TTableRow<TItem | TSearchItem>[] = getRows((row) => ({
    ...row,
    key: row.rowId as string,
  }));

  return (
    <div className="flex flex-1 h-full">
      <div className="grow h-full w-full relative">
        <div className="absolute top-0 left-0 w-full h-full overflow-y-auto">
          {rows.length ? (
            <div className="flex flex-col space-y-5 pt-4 pb-16 pl-5 pr-8">
              <ItemsTable
                rows={rows}
                resolvedNames={resolvedNames}
                show={{ location: true, status: false, date: false }}
                columnSizing={columnSizing_unstable}
              />
              <Pagination total={total} margin={UI_MARGIN_TOP} />
            </div>
          ) : (
            <p className="flex justify-center p-4 text-lg">No results found.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchResultsContent;
